import { useMemo, useState } from 'react';
import { Building2, Search, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { useOrgBatches, useOrgCompanyReadinessCompanies, useOrgCompanyReadiness } from '@/lib/hooks/useOrg';
import { BAND_META } from './bandMeta';

const SELECT_TRIGGER = 'h-[42px] w-auto min-w-[170px] rounded-[14px] border-[#ececec] bg-white px-[17px] text-sm font-medium text-[#6b7280]';

/**
 * Company/batch-scoped readiness table. When `fixedBatchId` is set (embedded
 * in a batch's own tabs), the batch selector is hidden and the query is
 * locked to that batch — otherwise it behaves like the standalone
 * /org/company-readiness page, with its own batch picker.
 */
export function CompanyReadinessView({ fixedBatchId }: { fixedBatchId?: string }) {
  const { data: companies, isLoading: companiesLoading } = useOrgCompanyReadinessCompanies();
  const { data: batches } = useOrgBatches();
  const [company, setCompany] = useState<string | undefined>(undefined);
  const [batch, setBatch] = useState('all');
  const [search, setSearch] = useState('');

  const effectiveBatchId = fixedBatchId ?? (batch === 'all' ? '' : batch);
  const { data, isLoading, isError } = useOrgCompanyReadiness(company, effectiveBatchId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.students ?? []).filter((s) => {
      if (!q) return true;
      return s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
    });
  }, [data, search]);

  const counts = useMemo(() => {
    const c = { ready: 0, developing: 0, needs_practice: 0 };
    (data?.students ?? []).forEach((s) => { c[s.band] += 1; });
    return c;
  }, [data]);

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger className={SELECT_TRIGGER}>
            <SelectValue placeholder={companiesLoading ? 'Loading…' : 'Select a company'} />
          </SelectTrigger>
          <SelectContent>
            {(companies ?? []).map((c) => (
              <SelectItem key={c.company} value={c.company}>
                {c.company} ({c.problem_count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!fixedBatchId && (
          <Select value={batch} onValueChange={setBatch}>
            <SelectTrigger className={SELECT_TRIGGER}><SelectValue placeholder="All Batches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Batches</SelectItem>
              {(batches ?? []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {company && (
          <div className="flex h-[42px] w-[260px] items-center gap-3 rounded-[14px] border border-[#ececec] bg-white px-[17px]">
            <Search className="h-[15px] w-[15px] flex-shrink-0 text-[#6b7280]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full border-none bg-transparent text-sm text-[#111827] placeholder:text-[rgba(107,114,128,0.5)] focus:outline-none"
            />
          </div>
        )}
      </div>

      {!company ? (
        <EmptyState
          icon={Building2}
          title="Pick a company"
          description="Select a company above to see how many of its tagged problems each student has solved."
        />
      ) : isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 rounded-[24px]" />
          <Skeleton className="h-96 rounded-[24px]" />
        </div>
      ) : isError ? (
        <p className="py-16 text-center text-sm text-[#9ca3af]">Couldn&apos;t load company readiness.</p>
      ) : (
        <>
          {/* Summary */}
          <div className="grid gap-4 sm:grid-cols-4">
            <OrgCard className="p-4">
              <p className="text-xs font-medium text-[#9ca3af]">Problems tagged</p>
              <p className="mt-1 text-2xl font-bold text-[#111827]">{data?.total ?? 0}</p>
            </OrgCard>
            {(['ready', 'developing', 'needs_practice'] as const).map((band) => (
              <OrgCard key={band} className="p-4">
                <div className="flex items-center gap-1.5">
                  <span className={cn('h-2 w-2 rounded-full', BAND_META[band].dotClass)} />
                  <p className="text-xs font-medium text-[#9ca3af]">{BAND_META[band].label}</p>
                </div>
                <p className="mt-1 text-2xl font-bold text-[#111827]">{counts[band]}</p>
              </OrgCard>
            ))}
          </div>

          <OrgCard className="overflow-hidden">
            {filtered.length === 0 ? (
              <p className="py-16 text-center text-sm text-[#9ca3af]">No students match your filters.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse">
                  <thead>
                    <tr className="border-b border-[#ececec]">
                      {['Student', 'Coverage', 'Progress', 'Status'].map((h) => (
                        <th key={h} className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s, i, arr) => (
                      <tr key={`${s.batch_id}-${s.user_id}`} className={cn(i !== arr.length - 1 && 'border-b border-[#ececec]')}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <OrgAvatar name={s.name} />
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                              <p className="truncate text-xs text-[#9ca3af]">{s.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm tabular-nums text-[#374151]">
                          {s.solved}/{s.total} <span className="text-[#9ca3af]">({Math.round(s.pct)}%)</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-1.5 w-32 overflow-hidden rounded-full bg-[#f3f4f6]">
                            <div
                              className={cn('h-full rounded-full', BAND_META[s.band].barClass)}
                              style={{ width: `${Math.min(100, s.pct)}%` }}
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', BAND_META[s.band].badgeClass)}>
                            <span className={cn('h-1.5 w-1.5 rounded-full', BAND_META[s.band].dotClass)} />
                            {BAND_META[s.band].label}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </OrgCard>

          <div className="flex items-start gap-3 rounded-[16px] border border-[#ececec] bg-white px-5 py-4">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#2563eb]" />
            <p className="text-sm text-[#6b7280]">
              Coverage is the share of this company&apos;s tagged problems each student has solved.
              Ready ≥ 70%, Developing ≥ 40%, otherwise Needs Practice.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
